import { useState } from 'react';
import { 
  Search, 
  Filter,
  MoreVertical,
  ArrowUpDown,
  Phone,
  Mail,
  MessageSquare,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Agent, AgentStatus } from '@/types/supervisor';

const agents: Agent[] = [];
import { cn } from '@/lib/utils';

interface TeamWorkloadProps {
  onAgentSelect?: (agent: Agent) => void;
}

export function TeamWorkload({ onAgentSelect }: TeamWorkloadProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'load' | 'sla'>('load');

  const filteredAgents = agents
    .filter(agent => {
      const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.department.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'load') return (b.currentLoad / b.maxCapacity) - (a.currentLoad / a.maxCapacity);
      if (sortBy === 'sla') return b.slaCompliance - a.slaCompliance;
      return 0;
    });

  const getStatusColor = (status: AgentStatus) => {
    switch (status) {
      case 'online': return 'bg-success';
      case 'busy': return 'bg-warning';
      case 'away': return 'bg-muted-foreground';
      case 'offline': return 'bg-muted';
    }
  };

  const getLoadColor = (load: number, max: number) => {
    const percentage = (load / max) * 100;
    if (percentage >= 90) return 'text-destructive';
    if (percentage >= 70) return 'text-warning';
    return 'text-success';
  };

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 animate-fade-in sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5 sm:space-y-1">
          <h1 className="text-xl font-bold sm:text-2xl">Team Workload</h1>
          <p className="text-sm text-muted-foreground sm:text-base">Monitor agent capacity and performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {agents.filter(a => a.status === 'online').length} online
          </Badge>
          <Badge variant="outline" className="text-xs">
            {agents.filter(a => a.status === 'busy').length} busy
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-2">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="busy">Busy</SelectItem>
                <SelectItem value="away">Away</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
              <SelectTrigger className="w-[140px]">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="load">By Workload</SelectItem>
                <SelectItem value="name">By Name</SelectItem>
                <SelectItem value="sla">By SLA</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Agent Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAgents.map((agent) => {
          const loadPercentage = (agent.currentLoad / agent.maxCapacity) * 100;
          
          return (
            <Card 
              key={agent.id} 
              className={cn(
                "border-2 hover:border-primary/50 transition-colors cursor-pointer",
                agent.status === 'offline' && "opacity-60"
              )}
              onClick={() => onAgentSelect?.(agent)}
            >
              <CardContent className="p-4 space-y-4">
                {/* Agent Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary">
                          {agent.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className={cn(
                        "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card",
                        getStatusColor(agent.status)
                      )} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm">{agent.name}</h3>
                      <p className="text-xs text-muted-foreground">{agent.department}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Send Message
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>
                        <Mail className="h-4 w-4 mr-2" />
                        Email
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Phone className="h-4 w-4 mr-2" />
                        Call
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Workload Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Workload</span>
                    <span className={cn("font-medium", getLoadColor(agent.currentLoad, agent.maxCapacity))}>
                      {agent.currentLoad}/{agent.maxCapacity} tickets
                    </span>
                  </div>
                  <Progress 
                    value={loadPercentage} 
                    className={cn(
                      "h-2",
                      loadPercentage >= 90 && "[&>div]:bg-destructive",
                      loadPercentage >= 70 && loadPercentage < 90 && "[&>div]:bg-warning"
                    )}
                  />
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-primary">{agent.ticketsOpen}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Open</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-warning">{agent.ticketsPending}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Pending</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-success">{agent.ticketsResolved}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Resolved</p>
                  </div>
                </div>

                {/* Footer Stats */}
                <div className="flex items-center justify-between pt-2 border-t text-xs">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Avg Resolution:</span>
                    <span className="font-medium">{agent.avgResolutionTime}</span>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-[10px]",
                      agent.slaCompliance >= 90 && "border-success text-success",
                      agent.slaCompliance >= 80 && agent.slaCompliance < 90 && "border-warning text-warning",
                      agent.slaCompliance < 80 && "border-destructive text-destructive"
                    )}
                  >
                    {agent.slaCompliance}% SLA
                  </Badge>
                </div>

                <p className="text-[10px] text-muted-foreground text-center">
                  Last activity: {agent.lastActivity}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
